import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthLayout } from "@/pages/Login";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const nextRoute = params.get("next"); // e.g. "pricing"
  const intendedTier = params.get("tier"); // e.g. "workshop"
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    consent_email_marketing: false,
    consent_sms_marketing: false,
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.consent_sms_marketing && !form.phone.trim()) {
      toast.error("Add your mobile number to opt into SMS.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success(`Welcome, ${data.name}. Your pathway starts now.`);
      // Resume checkout if the user came from a pricing card
      let storedTier = "";
      try { storedTier = localStorage.getItem("intended_tier") || ""; } catch {}
      const resumeTier = intendedTier || storedTier;
      if (nextRoute === "pricing" && resumeTier) {
        navigate(`/pricing?checkout=${encodeURIComponent(resumeTier)}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Preserve the intended tier through the Google redirect so /pricing can
    // auto-open the checkout modal when they come back.
    const target = (nextRoute === "pricing" && intendedTier)
      ? `/pricing?checkout=${encodeURIComponent(intendedTier)}`
      : "/dashboard";
    const redirectUrl = window.location.origin + target;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthLayout>
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Start your protocol</div>
      <h1 className="font-display text-4xl mb-8">Create your account.</h1>

      <Button onClick={handleGoogle} data-testid="google-signup-btn"
        className="w-full rounded-full py-6 mb-4 bg-white text-black hover:bg-white/90 border-0">
        Continue with Google
      </Button>

      <div className="flex items-center gap-3 text-[11px] text-white/30 my-6">
        <div className="flex-1 h-px bg-white/10" /> or email <div className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-xs text-white/60">Full name</Label>
          <Input id="name" value={form.name} onChange={update("name")} required
            data-testid="register-name"
            className="bg-[#0F0F0F] border-white/10 text-white mt-1 py-5 rounded-lg focus:border-[#FF8A00]"
            placeholder="Your name"/>
        </div>
        <div>
          <Label htmlFor="email" className="text-xs text-white/60">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={update("email")} required
            data-testid="register-email"
            className="bg-[#0F0F0F] border-white/10 text-white mt-1 py-5 rounded-lg focus:border-[#FF8A00]"
            placeholder="you@drthrive365.com"/>
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs text-white/60">
            Mobile number <span className="text-white/30">(optional — required for SMS updates)</span>
          </Label>
          <Input id="phone" type="tel" value={form.phone} onChange={update("phone")}
            data-testid="register-phone"
            className="bg-[#0F0F0F] border-white/10 text-white mt-1 py-5 rounded-lg focus:border-[#FF8A00]"
            placeholder="+1 555 123 4567"/>
        </div>
        <div>
          <Label htmlFor="password" className="text-xs text-white/60">Password</Label>
          <Input id="password" type="password" value={form.password} onChange={update("password")}
            required minLength={6}
            data-testid="register-password"
            className="bg-[#0F0F0F] border-white/10 text-white mt-1 py-5 rounded-lg focus:border-[#FF8A00]"
            placeholder="At least 6 characters"/>
        </div>

        {/* TCPA / CAN-SPAM consent */}
        <div className="rounded-xl border border-white/8 bg-[#0F0F0F] p-4 space-y-3 mt-2">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-email-label">
            <Checkbox
              checked={form.consent_email_marketing}
              onCheckedChange={(v) => setForm({ ...form, consent_email_marketing: !!v })}
              data-testid="register-consent-email"
              className="mt-0.5 border-white/20"
            />
            <span className="text-xs text-white/65 leading-relaxed">
              <strong className="text-white/85">Yes, email me</strong> protocol updates, new lessons, and occasional offers from Dr. Slava. You can unsubscribe at any time via the link in every email.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-sms-label">
            <Checkbox
              checked={form.consent_sms_marketing}
              onCheckedChange={(v) => setForm({ ...form, consent_sms_marketing: !!v })}
              data-testid="register-consent-sms"
              className="mt-0.5 border-white/20"
            />
            <span className="text-xs text-white/65 leading-relaxed">
              <strong className="text-white/85">Yes, text me</strong> reminders and occasional protocol drops.
              By checking this box, you agree to receive recurring automated marketing text messages from Dr Thrive 365 at the mobile number provided. Consent is not a condition of purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply <span className="font-mono">STOP</span> to cancel, <span className="font-mono">HELP</span> for help. See our <Link to="/privacy" className="text-[#FF8A00] hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#FF8A00] hover:underline">Terms</Link>.
            </span>
          </label>
        </div>

        <Button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full rounded-full py-6 bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white">
          {loading ? "Creating…" : (<>Create account <ArrowRight className="ml-2" size={16} /></>)}
        </Button>
      </form>

      <p className="text-sm text-white/50 mt-8">
        Already a member?{" "}
        <Link to="/login" className="text-[#FF8A00] hover:underline" data-testid="go-login">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
